import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  renderBreakdown, renderLabelBreakdown, renderLoanNumbers, formatWeight, formatCurrency,
  type LoanBreakdown,
} from './ObLoanBreakdown';
import './LoanDataDisplay.scss';

interface ObLoanReportItem {
  serialNumber: number;
  date: string | null;
  branch: string;
  loanNumber: string;
  pledgeId: string;
  obNumber: string;
  obBankName: string;
  accountHolderName: string;
  grossWeight: number;
  netWeight: number;
  loanBreakdown: LoanBreakdown[];
  pledgeAmount: number;
  onlinePrincipal: number;
  offlinePrincipal: number;
  pledgeType: string;
  loanType: string;
  obCharges: number;
  remarks: string;
  interestRate: string;
}

interface ObLoanReportProps {
  data: ObLoanReportItem[];
}

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatText = (val: string) => (val != null && val.length > 0 ? val : '-');

const columns: ColumnsType<ObLoanReportItem> = [
  {
    title: 'S.No',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 60,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 120,
    className: 'date-cell',
    render: formatDate,
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Org Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: renderLoanNumbers,
  },
  {
    title: 'Pledge ID',
    dataIndex: 'pledgeId',
    key: 'pledgeId',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'OB Number',
    dataIndex: 'obNumber',
    key: 'obNumber',
  },
  {
    title: 'OB Bank Name',
    dataIndex: 'obBankName',
    key: 'obBankName',
  },
  {
    title: 'A/C Holder Name',
    dataIndex: 'accountHolderName',
    key: 'accountHolderName',
  },
  {
    title: 'GW (g)',
    dataIndex: 'grossWeight',
    key: 'grossWeight',
    render: (val: number, record) =>
      renderBreakdown('grossWeight', formatWeight, val, record.loanBreakdown),
  },
  {
    title: 'NW (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number, record) =>
      renderBreakdown('netWeight', formatWeight, val, record.loanBreakdown),
  },
  {
    title: 'Pledge Amount',
    dataIndex: 'pledgeAmount',
    key: 'pledgeAmount',
    render: (val: number, record) =>
      renderBreakdown('pledgeAmount', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Online Principal',
    dataIndex: 'onlinePrincipal',
    key: 'onlinePrincipal',
    render: (val: number, record) =>
      renderBreakdown('onlinePrincipal', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Offline Principal',
    dataIndex: 'offlinePrincipal',
    key: 'offlinePrincipal',
    render: (val: number, record) =>
      renderBreakdown('offlinePrincipal', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Type',
    dataIndex: 'loanType',
    key: 'loanType',
    render: (val: string, record) =>
      renderLabelBreakdown('loanType', val, record.loanBreakdown),
    filters: [
      { text: 'Direct', value: 'Direct' },
      { text: 'Repledge', value: 'Repledge' },
    ],
    onFilter: (value, record) =>
      (record.loanType ?? '').split(', ').includes(String(value)),
  },
  {
    title: 'OB Charges (DC)',
    dataIndex: 'obCharges',
    key: 'obCharges',
    render: (val: number, record) =>
      renderBreakdown('obCharges', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Remarks',
    dataIndex: 'remarks',
    key: 'remarks',
  },
  {
    title: 'Interest Rate',
    dataIndex: 'interestRate',
    key: 'interestRate',
    render: formatText,
  },
];

const ObLoanReportDisplay: React.FC<ObLoanReportProps> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          grossWeight: acc.grossWeight + (item.grossWeight ?? 0),
          netWeight: acc.netWeight + (item.netWeight ?? 0),
          pledgeAmount: acc.pledgeAmount + (item.pledgeAmount ?? 0),
          onlinePrincipal: acc.onlinePrincipal + (item.onlinePrincipal ?? 0),
          offlinePrincipal: acc.offlinePrincipal + (item.offlinePrincipal ?? 0),
          obCharges: acc.obCharges + (item.obCharges ?? 0),
        }),
        {
          grossWeight: 0,
          netWeight: 0,
          pledgeAmount: 0,
          onlinePrincipal: 0,
          offlinePrincipal: 0,
          obCharges: 0,
        },
      ),
    [data],
  );

  return (
    <div className="loan-data-container">
      <h2>OB Loan Report</h2>
      <Table
        columns={columns}
        dataSource={data.map((item) => ({
          ...item,
          key: `${item.pledgeId}-${item.serialNumber}`,
        }))}
        rowKey="key"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={8}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8}>
                <strong>{totals.grossWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9}>
                <strong>{totals.netWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{formatCurrency(totals.pledgeAmount)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11}>
                <strong>{formatCurrency(totals.onlinePrincipal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12}>
                <strong>{formatCurrency(totals.offlinePrincipal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={14}>
                <strong>{formatCurrency(totals.obCharges)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={15}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={16}>-</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default ObLoanReportDisplay;
